import { forwardRef } from 'react';
import { PolymorphicRef } from '@mofid-securities/uikit';
import Button from '@/components/Button';
import { classnames } from '@/utils/classnames';
import type {
  ISelectButtonProps,
  TSelectButtonValue,
} from './SelectButton.types';

export const SelectButton = forwardRef(
  <TValue extends TSelectButtonValue = TSelectButtonValue>(
    {
      isSelected,
      onClick,
      children,
      className,
      disabled = false,
      groupVariant,
      ...rest
    }: ISelectButtonProps<TValue>,
    ref: PolymorphicRef<'button'>
  ): JSX.Element => {
    const isGroupVariantPrimary = groupVariant === 'primary';

    let variant: ISelectButtonProps['variant'] = isGroupVariantPrimary
      ? 'link'
      : 'outline';
    let color: ISelectButtonProps['color'] = 'primary';

    if (isSelected) {
      variant = 'default';
      color = isGroupVariantPrimary ? 'primary' : 'secondary';
    }

    return (
      <Button
        ref={ref}
        className={classnames(
          '!h-8 flex-1 rounded-md !px-1 !text-xs font-medium',
          !isSelected && '!border-gray-50',
          isSelected && 'pointer-events-none',
          className
        )}
        color={color}
        disabled={disabled}
        variant={variant}
        onClick={onClick}
        {...rest}
      >
        {children}
      </Button>
    );
  }
) as <TValue extends TSelectButtonValue = TSelectButtonValue>(
  __props: ISelectButtonProps<TValue> & { ref?: PolymorphicRef<'button'> }
) => JSX.Element;

// @ts-ignore
SelectButton.displayName = 'SelectButton';
